import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import BusinessCard from "@/components/BusinessCard";
import UploadButton from "@/components/UploadButton";
import WallPostsPanel from "@/components/WallPostsPanel";
import VerificationChecklist from "@/components/VerificationChecklist";
import MembershipCard from "@/components/MembershipCard";
import ProPlusLocked from "@/components/ProPlusLocked";
import ProfileViewsPanel from "@/components/ProfileViewsPanel";
import { Eye, Users, Star, Pin, Image as ImageIcon, MessageSquare, Sparkles, TrendingUp, Plus, X, Send, Briefcase, CheckCircle2, Award, Clock as ClockIcon, Bell, CreditCard, Ticket, ClipboardList, Link2, UserCheck } from "lucide-react";
import BetaBanner from "@/components/BetaBanner";
import { toast, Toaster } from "sonner";
import { statusText, statusDotColor } from "@/lib/businessStatus";

export default function Dashboard() {
  const { user, refresh, workedWith } = useAuth();
  const navigate = useNavigate();
  const [saved, setSaved] = useState([]);
  const [following, setFollowing] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [proData, setProData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [newPost, setNewPost] = useState("");
  const [newPostImage, setNewPostImage] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [openClients, setOpenClients] = useState([]);
  const [showOutreach, setShowOutreach] = useState(null);
  const [outreachText, setOutreachText] = useState("");
  const [hires, setHires] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [emailPrefs, setEmailPrefs] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [reactionsCfg, setReactionsCfg] = useState([]);
  const [promos, setPromos] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [confirmedWork, setConfirmedWork] = useState([]);
  const [showAllWork, setShowAllWork] = useState(false);
  const [incomingConnections, setIncomingConnections] = useState([]);
  const [connectionsCount, setConnectionsCount] = useState(0);
  const [threads, setThreads] = useState([]);
  const [activeRequestsCount, setActiveRequestsCount] = useState(0);
  const [matched, setMatched] = useState(null);

  useEffect(() => {
    api.get("/config/public").then(r => setReactionsCfg(r.data.post_reactions || [])).catch(() => {});
  }, []);
  const reactionEmoji = (key) => (reactionsCfg.find(r => r.key === key) || {}).emoji || "👍";

  useEffect(() => {
    if (!user) return;
    if (user.role === "client") {
      // Client Dashboard has been removed — send clients to their profile (My Profile).
      navigate(`/client/${user.id}`, { replace: true });
      return;
    } else if (user.role === "pro") {
      api.get("/analytics/me").then(r => setAnalytics(r.data));
      api.get(`/pros/${user.id}`).then(r => {
        setProData(r.data); setForm(r.data);
        setPromos(r.data.promotions || []);
      });
      api.get(`/posts/by-pro/${user.id}`).then(r => setPosts(r.data));
      api.get("/posts/engagement/summary").then(r => setEngagement(r.data)).catch(() => {});
      api.get("/connections/incoming").then(r => setIncoming(r.data.requests || [])).catch(() => {});
      api.get("/connections/confirmed").then(r => setConfirmedWork(r.data.connections || [])).catch(() => {});
      api.get(`/hires/pro/${user.id}/stats`).then(r => setLoyalty(r.data)).catch(() => {});
      api.get("/hires/mine").then(r => setHires(r.data)).catch(() => {});
      api.get("/users/me/email-prefs").then(r => setEmailPrefs(r.data)).catch(() => {});
      if (user.pro_plus_access) {
        api.get("/requests/matched").then(r => setMatched(r.data)).catch(() => {});
        api.get("/clients/open").then(r => setOpenClients(r.data)).catch(() => {});
      }
    } else if (user.role === "admin") {
      navigate("/admin");
    }
  }, [user, navigate]);

  const refreshHires = () => api.get("/hires/mine").then(r => setHires(r.data));
  const respondConnection = async (id, action) => {
    try {
      await api.post(`/connections/${id}/${action}`);
      setIncoming(list => list.filter(c => c.id !== id));
      if (action === "confirm") api.get("/connections/confirmed").then(r => setConfirmedWork(r.data.connections || [])).catch(() => {});
      toast.success(action === "confirm" ? "Confirmed — you're Worked Together" : "Marked as not correct");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const respondClientConnection = async (id, action) => {
    try {
      await api.post(`/client-connections/${id}/${action}`);
      setIncomingConnections(list => list.filter(c => c.id !== id));
      if (action === "accept") setConnectionsCount(c => c + 1);
      toast.success(action === "accept" ? "Connected!" : "Declined");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const proHireAction = async (hireId, action, payload = {}) => {
    try {
      await api.post(`/hires/${hireId}/${action}`, payload);
      refreshHires();
      if (user?.role === "pro") {
        api.get(`/hires/pro/${user.id}/stats`).then(r => setLoyalty(r.data)).catch(() => {});
      }
      toast.success("Updated");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (user === null) return <div className="min-h-screen grid place-items-center"><p>Loading…</p></div>;
  if (!user) { navigate("/login"); return null; }
  if (user.role === "client") return <div className="min-h-screen grid place-items-center"><p>Loading…</p></div>;

  const submitPost = async (e) => {
    e.preventDefault();
    if (!newPost.trim()) return;
    await api.post("/posts", { content: newPost, image_url: newPostImage });
    setNewPost(""); setNewPostImage("");
    const r = await api.get(`/posts/by-pro/${user.id}`);
    setPosts(r.data);
    toast.success("Posted to your feed");
  };

  const sendOutreach = async () => {
    if (!showOutreach || !outreachText.trim()) return;
    try {
      await api.post("/messages", { to_user_id: showOutreach.id, content: outreachText });
      toast.success("Outreach sent");
      setShowOutreach(null); setOutreachText("");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const savePromotions = async () => {
    try {
      const r = await api.put("/pros/me/promotions", { promotions: promos });
      setPromos(r.data.promotions || []);
      toast.success("Promotions saved");
    } catch { toast.error("Could not save promotions"); }
  };

  const toggleEmailPref = async (key, value) => {
    try {
      const r = await api.patch("/users/me/email-prefs", { [key]: value });
      setEmailPrefs(r.data);
    } catch { toast.error("Could not update preferences"); }
  };

  const openCustomerPortal = async () => {
    try {
      const r = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not open billing portal");
    }
  };

  const saveProfile = async () => {
    try {
      const payload = {
        business_name: form.business_name, category: form.category, bio: form.bio,
        phone: form.phone, website: form.website, city: form.city,
        service_radius: parseInt(form.service_radius) || 25,
        logo_url: form.logo_url, avatar_url: form.avatar_url, cover_url: form.cover_url,
        languages: typeof form.languages === "string" ? form.languages.split(",").map(s=>s.trim()) : (form.languages||["English"]),
        years_in_business: parseInt(form.years_in_business) || 0,
        services: form.services || [],
        hours: form.hours, response_time: form.response_time,
        card_color: form.card_color, pin_color: form.pin_color,
        faq: (form.faq || []).filter(f => f.q && f.a),
        address: form.address || "",
      };
      const r = await api.put("/pros/me", payload);
      setProData(r.data); setEditing(false);
      toast.success("Profile saved");
    } catch (e) { toast.error("Save failed"); }
  };

  return (
    <div>
      <Header/>
      <Toaster position="top-center"/>
      <div className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="text-4xl font-bold mb-2" style={{fontFamily:'Outfit'}}>Hi, {user.name?.split(" ")[0]} 👋</h1>
        <p className="text-[#222]/60 mb-8">
          {user.role === "client" ? "Your saved pros and conversations." : "Your business at a glance."}
        </p>

        {user.role === "client" && (
          <>
            {/* Actionable stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="private-home-stats">
              {[
                { label: "Active requests", value: activeRequestsCount, Icon: ClipboardList, color: "#2E6B57", to: "/feed?tab=wall" },
                { label: "Unread messages", value: threads.reduce((s, t) => s + (t.unread || 0), 0), Icon: MessageSquare, color: "#F59E0B", to: "/messages" },
                { label: "Connections", value: connectionsCount, Icon: Link2, color: "#C79B6B", to: "/connections" },
                { label: "Board pins", value: saved.length, Icon: Pin, color: "#245544", to: "/board" },
              ].map((s, i) => (
                <button key={i} onClick={() => navigate(s.to)} className="bg-white p-5 rounded-2xl shadow-sm border border-black/5 text-left hover:border-black/15 transition" data-testid={`home-stat-${s.label.toLowerCase().replace(/ /g, "-")}`}>
                  <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-[#222]/50"><s.Icon size={14} style={{ color: s.color }}/> {s.label}</div>
                  <p className="text-3xl font-bold" style={{ fontFamily: "Outfit" }}>{s.value}</p>
                </button>
              ))}
            </div>

            {/* Incoming connection requests */}
            {incomingConnections.length > 0 && (
              <section className="mb-8 bg-white rounded-2xl p-6 shadow-sm border border-[#2E6B57]/40" data-testid="incoming-connections">
                <h3 className="text-xl font-bold mb-1 flex items-center gap-2" style={{ fontFamily: "Outfit" }}><Link2 size={18} className="text-[#C79B6B]"/> Connection requests</h3>
                <p className="text-sm text-[#222]/60 mb-4">Neighbors who'd like to connect with you.</p>
                <div className="space-y-2">
                  {incomingConnections.map(c => (
                    <div key={c.id} className="border border-black/5 rounded-xl p-3 flex items-center gap-3" data-testid={`incoming-conn-${c.id}`}>
                      <div className="w-9 h-9 rounded-full bg-[#F2F4F2] grid place-items-center font-bold overflow-hidden shrink-0">
                        {c.requester.avatar_url ? <img src={c.requester.avatar_url} className="w-full h-full object-cover" alt=""/> : c.requester.name?.[0]}
                      </div>
                      <p className="flex-1 min-w-0 font-semibold text-sm truncate">{c.requester.name}</p>
                      <button onClick={() => respondClientConnection(c.id, "accept")} className="text-xs btn-lime px-3 py-1.5 flex items-center gap-1" data-testid={`incoming-conn-accept-${c.id}`}><CheckCircle2 size={13}/> Accept</button>
                      <button onClick={() => respondClientConnection(c.id, "decline")} className="text-xs px-3 py-1.5 rounded-full bg-black/5 hover:bg-black/10" data-testid={`incoming-conn-decline-${c.id}`}>Decline</button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Quick actions */}
            <div className="flex flex-wrap gap-3 mb-8" data-testid="private-home-quick-actions">
              <button onClick={() => navigate("/feed?tab=wall")} className="btn-lime px-5 py-2.5 text-sm flex items-center gap-2" data-testid="home-post-wall-btn"><Plus size={15}/> Post to Wall</button>
              <button onClick={() => navigate("/search")} className="px-5 py-2.5 text-sm rounded-full bg-white border border-black/10 hover:bg-black/5 font-semibold" data-testid="home-browse-btn">Browse Pros</button>
              <button onClick={() => navigate("/board")} className="px-5 py-2.5 text-sm rounded-full bg-white border border-black/10 hover:bg-black/5 font-semibold" data-testid="home-open-board-btn">Open My Board</button>
            </div>

            {/* Recent messages preview */}
            {threads.length > 0 && (
              <section className="mb-8" data-testid="home-messages-preview">
                <div className="flex items-end justify-between mb-3">
                  <h2 className="text-xl font-bold" style={{ fontFamily: "Outfit" }}>Recent messages</h2>
                  <button onClick={() => navigate("/messages")} className="text-xs text-[#245544] font-semibold">View all →</button>
                </div>
                <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
                  {threads.slice(0, 3).map(t => (
                    <button key={t.thread_id} onClick={() => navigate("/messages")} className="w-full flex items-center gap-3 p-4 border-b border-black/5 last:border-0 text-left hover:bg-black/[0.02]" data-testid={`home-thread-${t.thread_id}`}>
                      <div className="w-9 h-9 rounded-full bg-[#F2F4F2] grid place-items-center font-bold shrink-0 overflow-hidden">
                        {t.other_user.avatar_url ? <img src={t.other_user.avatar_url} className="w-full h-full object-cover" alt=""/> : t.other_user.name?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{t.other_user.name}</p>
                        <p className="text-xs text-[#222]/50 truncate">{t.last_message.content}</p>
                      </div>
                      {t.unread > 0 && <span className="text-[10px] font-bold bg-[#2E6B57] text-[#FFFFFF] rounded-full px-2 py-0.5">{t.unread}</span>}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {hires.length > 0 && (
              <section className="mb-8" data-testid="client-hires-section">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{fontFamily:'Outfit'}}><Briefcase size={18}/> Recent jobs ({hires.length})</h2>
                <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
                  {hires.map(h => (
                    <div key={h.id} className="p-4 border-b border-black/5 last:border-0 flex items-center gap-3" data-testid={`client-hire-${h.id}`}>
                      <div className={`w-2 h-2 rounded-full ${h.status==="completed"?"bg-[#2E6B57]":h.status==="canceled"?"bg-red-400":"bg-[#F59E0B]"}`}/>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{h.service_name}</p>
                        <p className="text-xs text-[#222]/60 truncate">With {h.pro_name} · {new Date(h.requested_at).toLocaleDateString()}</p>
                      </div>
                      <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-black/5">{h.status}</span>
                      <button onClick={() => navigate(`/pro/${h.pro_user_id}`)} className="text-xs text-[#245544] font-semibold" data-testid={`client-hire-open-${h.id}`}>View pro →</button>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <WallPostsPanel/>
          </>
        )}

        {user.role === "pro" && analytics && (
          <>
            <MembershipCard user={user}/>
            <VerificationChecklist/>
            {incoming.length > 0 && (
              <section className="mb-6 bg-white rounded-2xl p-6 shadow-sm border border-[#2E6B57]/40" data-testid="work-confirmations">
                <h3 className="text-xl font-bold mb-1 flex items-center gap-2" style={{fontFamily:'Outfit'}}><Award size={18} className="text-[#C79B6B]"/> Work Confirmations</h3>
                <p className="text-sm text-[#222]/60 mb-4">A neighbor says you helped them. Confirming adds your Worked Together relationship.</p>
                <div className="space-y-2">
                  {incoming.map(c => (
                    <div key={c.id} className="border border-black/5 rounded-xl p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`wt-request-${c.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{c.client_name || "A neighbor"}</p>
                        <p className="text-xs text-[#222]/55 truncate">
                          {c.request_title ? `Request: ${c.request_title}` : "says you worked together"}
                          {c.created_at ? ` · ${new Date(c.created_at).toLocaleDateString()}` : ""}
                        </p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <button onClick={() => respondConnection(c.id, "confirm")} className="text-xs btn-lime px-3 py-1.5 flex items-center gap-1" data-testid={`wt-confirm-${c.id}`}><CheckCircle2 size={13}/> Yes, we worked together</button>
                        <button onClick={() => respondConnection(c.id, "decline")} className="text-xs px-3 py-1.5 rounded-full bg-black/5 hover:bg-black/10 flex items-center gap-1" data-testid={`wt-decline-${c.id}`}><X size={13}/> This isn't correct</button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
            {confirmedWork.length > 0 && (
              <section className="mb-6 bg-white rounded-2xl p-6 shadow-sm border border-black/5" data-testid="worked-together-history">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-bold flex items-center gap-2" style={{fontFamily:'Outfit'}}><CheckCircle2 size={17} className="text-[#245544]"/> Worked Together ({confirmedWork.length})</h3>
                  {confirmedWork.length > 3 && (
                    <button onClick={() => setShowAllWork(s => !s)} className="text-xs text-[#245544] font-semibold" data-testid="worked-together-view-all">{showAllWork ? "Show less" : "View all →"}</button>
                  )}
                </div>
                <div className="space-y-2">
                  {(showAllWork ? confirmedWork : confirmedWork.slice(0, 3)).map(c => (
                    <div key={c.id} className="border border-black/5 rounded-xl p-3 flex items-center gap-3" data-testid={`worked-together-${c.id}`}>
                      <div className="w-9 h-9 rounded-full bg-[#F2F4F2] grid place-items-center font-bold overflow-hidden shrink-0">
                        {c.client_avatar ? <img src={c.client_avatar} className="w-full h-full object-cover" alt=""/> : c.client_name?.[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{c.client_name}</p>
                        <p className="text-xs text-[#222]/50 truncate">{c.request_title || "Worked together"}{c.confirmed_at ? ` · ${new Date(c.confirmed_at).toLocaleDateString()}` : ""}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
            {!user.is_pro_plus && <BetaBanner/>}
            {user.pro_plus_status === "past_due" && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-6 flex items-start gap-3" data-testid="past-due-banner">
                <div className="w-8 h-8 rounded-full bg-amber-200 grid place-items-center shrink-0 text-amber-800 font-bold">!</div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-amber-900">Your last payment didn't go through</p>
                  <p className="text-sm text-amber-800/80">Pro Plus is still active while Stripe retries the charge. Update your payment method in the billing portal to avoid losing premium features.</p>
                </div>
                <button onClick={openCustomerPortal} className="btn-dark px-4 py-2 text-sm shrink-0" data-testid="past-due-fix-btn">Update card</button>
              </div>
            )}
            <ProfileViewsPanel analytics={analytics} hasProPlus={!!user.pro_plus_access}/>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {[
                {label:"Followers", value: analytics.followers, Icon: Users, color:"#F59E0B"},
                {label:"Saved by", value: analytics.saved, Icon: Pin, color:"#C79B6B"},
                {label:"Reviews", value: `${analytics.rating?.toFixed(1) || "\u2014"} (${analytics.reviews})`, Icon: Star, color:"#EF4444"},
              ].map((s, i) => (
                <div key={i} className="bg-white p-5 rounded-2xl shadow-sm border border-black/5" data-testid={`stat-${s.label.toLowerCase().replace(/ /g,"-")}`}>
                  <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-[#222]/50"><s.Icon size={14} style={{color:s.color}}/> {s.label}</div>
                  <p className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>{s.value}</p>
                </div>
              ))}
            </div>

            {/* Loyalty stats — visible to all pros (free & plus). Drives Neighborhood Trusted progress. */}
            {loyalty && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-black/5">
                  <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-[#222]/50"><Briefcase size={14}/> Completed jobs</div>
                  <p className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>{loyalty.completed_jobs}</p>
                </div>
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-black/5">
                  <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-[#222]/50"><Users size={14}/> Lifetime clients</div>
                  <p className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>{loyalty.lifetime_clients}</p>
                </div>
                <div className="bg-white p-5 rounded-2xl shadow-sm border border-black/5">
                  <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider text-[#222]/50"><Star size={14}/> Hire-again rate</div>
                  <p className="text-3xl font-bold" style={{fontFamily:'Outfit'}}>{loyalty.hire_again_pct}%</p>
                </div>
                <div className={`p-5 rounded-2xl shadow-sm border ${loyalty.neighborhood_trusted ? "bg-[#C79B6B] text-white border-[#A07A4E]" : "bg-white border-black/5"}`} data-testid="loyalty-trusted-card">
                  <div className="flex items-center gap-2 mb-2 text-xs uppercase tracking-wider opacity-70"><Award size={14}/> Neighborhood Trusted</div>
                  <p className="text-base font-bold" style={{fontFamily:'Outfit'}}>{loyalty.neighborhood_trusted ? "Earned ✨" : "Earn it with repeat clients"}</p>
                </div>
              </div>
            )}

            {/* Engagement — reactions & chip-comments on the pro's feed */}
            {engagement && (
              <section className="mb-10 bg-white p-6 rounded-2xl shadow-sm border border-black/5" data-testid="engagement-panel">
                <div className="mb-4">
                  <h3 className="text-xl font-bold flex items-center gap-2" style={{fontFamily:'Outfit'}}><TrendingUp size={18} className="text-[#2E6B57]"/> Your engagement</h3>
                  <p className="text-xs text-[#222]/60">Reactions and comments neighbors left on your feed.</p>
                </div>
                <div className="grid grid-cols-3 gap-3 mb-5">
                  {[
                    {label:"Reactions", value: engagement.totals.reactions},
                    {label:"Comments", value: engagement.totals.comments},
                    {label:"Interested", value: engagement.totals.intent},
                  ].map((s,i) => (
                    <div key={i} className="bg-[#F2F4F2] rounded-xl p-4 text-center border border-black/5" data-testid={`engagement-stat-${s.label.toLowerCase()}`}>
                      <p className="text-2xl font-bold" style={{fontFamily:'Outfit'}}>{s.value}</p>
                      <p className="text-xs uppercase tracking-wider text-[#222]/50">{s.label}</p>
                    </div>
                  ))}
                </div>
                {engagement.is_pro_plus ? (
                  engagement.leads.length === 0 ? (
                    <p className="text-sm text-[#222]/50 py-2">No engagement yet. Post to your feed to get noticed.</p>
                  ) : (
                    <div className="space-y-2">
                      {engagement.leads.map((l, i) => (
                        <div key={i} className="border border-black/5 rounded-xl p-3 flex items-center gap-3" data-testid={`engagement-lead-${i}`}>
                          <div className="w-9 h-9 rounded-full bg-[#F2F4F2] grid place-items-center text-xs font-bold overflow-hidden shrink-0">
                            {l.avatar ? <img src={l.avatar} alt="" className="w-full h-full object-cover"/> : (l.name || "?")[0]}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm truncate">{l.name || "Someone"}</p>
                            <p className="text-xs text-[#222]/60 truncate">
                              {l.type === "reaction"
                                ? `reacted ${reactionEmoji(l.key)}`
                                : `${l.bucket === "intent" ? "asked" : "said"}: "${l.chip}"`}
                              {" · "}on "{l.post_excerpt}…"
                            </p>
                          </div>
                          {l.bucket === "intent" && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] shrink-0">Lead</span>}
                          <button onClick={() => setShowOutreach({ id: l.user_id, name: l.name, looking_for: l.type === "comment" ? l.chip : "engaged with your post" })} className="text-xs btn-lime px-3 py-1 shrink-0 flex items-center gap-1" data-testid={`engagement-dm-${i}`}>
                            <Send size={11}/> Message
                          </button>
                        </div>
                      ))}
                    </div>
                  )
                ) : (
                  <ProPlusLocked
                    title="Who engaged with your work"
                    benefit="See which neighbors interacted with your posts and, when they allow it, reach out directly."
                    Icon={UserCheck}
                    testId="locked-engagement-leads"
                    preview={
                      <div className="space-y-2">
                        {["A neighbor asked about your work", "A neighbor reacted to your post"].map((t, i) => (
                          <div key={i} className="border border-black/5 bg-white rounded-xl p-3 flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-[#F2F4F2]"/>
                            <p className="text-xs text-[#222]/70">{t}</p>
                          </div>
                        ))}
                      </div>
                    }
                  />
                )}
              </section>
            )}

            {/* Business status — READ-ONLY summary. The single editing surface
                is Edit profile, so there is never a second form for one setting. */}
            <div className="mb-10 max-w-md">
              <div className="bg-white p-5 rounded-2xl shadow-sm border border-black/5" data-testid="business-status-summary">
                <h4 className="font-bold mb-2 text-sm flex items-center gap-2" style={{fontFamily:'Outfit'}}>
                  <span className="w-2 h-2 rounded-full" style={{background: statusDotColor(proData?.business_status) || "#ccc"}}/>
                  Business status
                </h4>
                <p className="text-sm text-[#222]/75" data-testid="business-status-current">
                  {statusText(proData?.business_status) || "No banner — your profile shows as normal."}
                </p>
                <button onClick={() => navigate(`/pro/${user.id}?edit=profile`)}
                  className="mt-3 text-xs font-semibold text-[#245544] hover:underline"
                  data-testid="business-status-edit-link">
                  Edit in profile →
                </button>
              </div>
            </div>

            {/* Incoming hires for this pro */}
            {hires.length > 0 && (
              <section className="bg-white p-6 rounded-2xl shadow-sm border border-black/5 mb-10" data-testid="pro-hires-section">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2" style={{fontFamily:'Outfit'}}><Briefcase size={18}/> Your jobs ({hires.length})</h3>
                <div className="space-y-2">
                  {hires.map(h => (
                    <div key={h.id} className="border border-black/5 rounded-xl p-3 flex flex-col md:flex-row md:items-center gap-3" data-testid={`hire-row-${h.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm truncate">{h.service_name}</p>
                        <p className="text-xs text-[#222]/60 truncate">From {h.client_name} {h.amount ? `· $${h.amount}` : ""}</p>
                      </div>
                      <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full ${h.status==="completed"?"bg-[#2E6B57] text-[#FFFFFF]":h.status==="canceled"?"bg-red-100 text-red-700":"bg-black/10"}`}>{h.status}</span>
                      <div className="flex gap-1 flex-wrap">
                        {h.status === "requested" && <button onClick={() => proHireAction(h.id, "accept", {})} className="text-xs btn-lime px-3 py-1" data-testid={`hire-accept-${h.id}`}>Accept</button>}
                        {h.status === "accepted" && <button onClick={() => proHireAction(h.id, "start", {})} className="text-xs px-3 py-1 rounded-full bg-black/5" data-testid={`hire-start-${h.id}`}>Start</button>}
                        {(h.status === "accepted" || h.status === "in_progress") && <button onClick={() => proHireAction(h.id, "complete", {})} className="text-xs btn-dark px-3 py-1" data-testid={`hire-complete-${h.id}`}>Mark complete</button>}
                        {(h.status === "requested" || h.status === "accepted" || h.status === "in_progress") && <button onClick={() => proHireAction(h.id, "cancel", {})} className="text-xs px-3 py-1 rounded-full bg-black/5" data-testid={`hire-cancel-${h.id}`}>Cancel</button>}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Requests. Access + responding are free for everyone; only the
                AUTOMATIC matching/surfacing below is a Pro Plus convenience. */}
            {user.pro_plus_access ? (
              <section className="bg-white p-6 rounded-2xl shadow-sm border border-black/5 mb-10" data-testid="matched-requests-section">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h3 className="text-xl font-bold flex items-center gap-2" style={{fontFamily:'Outfit'}}><ClipboardList size={18} className="text-[#245544]"/> Requests for your services</h3>
                    <p className="text-xs text-[#222]/60">Open requests matching your categories and service area, surfaced automatically.</p>
                  </div>
                  <span className="text-3xl font-bold shrink-0" style={{fontFamily:'Outfit'}} data-testid="matched-requests-count">{matched?.count ?? 0}</span>
                </div>
                {matched?.requests?.length > 0 ? (
                  <div className="max-h-72 overflow-y-auto pr-1 space-y-2 mb-4" data-testid="matched-requests-list">
                    {matched.requests.map(s2 => (
                      <button key={s2.id} onClick={() => navigate(`/requests/${s2.id}`)}
                        className="w-full text-left border border-black/5 rounded-xl p-3 hover:border-black/15 transition"
                        data-testid={`matched-request-${s2.id}`}>
                        <p className="font-semibold text-sm truncate">{s2.title}</p>
                        <p className="text-xs text-[#222]/55 truncate">{s2.category || "General"} · ~{s2.approx_distance} mi away</p>
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-[#222]/55 mb-4" data-testid="matched-requests-empty">
                    No open requests match your categories and area right now — we'll surface them here as neighbors post.
                  </p>
                )}
                <button onClick={() => navigate("/requests")} className="btn-lime px-5 py-2 text-sm" data-testid="matched-requests-view-btn">View all requests</button>
              </section>
            ) : (
              <section className="bg-white p-6 rounded-2xl shadow-sm border border-black/5 mb-10" data-testid="browse-requests-card">
                <h3 className="text-xl font-bold flex items-center gap-2 mb-1" style={{fontFamily:'Outfit'}}><ClipboardList size={18} className="text-[#245544]"/> Browse Requests</h3>
                <p className="text-sm text-[#222]/60 mb-4">Find neighbors looking for services and filter Requests by category and area — free for every Professional to answer.</p>
                <button onClick={() => navigate("/requests")} className="btn-lime px-5 py-2 text-sm" data-testid="browse-requests-btn">View Requests</button>
              </section>
            )}

            {user.pro_plus_access ? (
              <section className="bg-white p-6 rounded-2xl shadow-sm border border-black/5 mb-10" data-testid="open-clients-section">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-xl font-bold flex items-center gap-2" style={{fontFamily:'Outfit'}}><Sparkles className="text-[#2E6B57]" size={18}/> Clients looking for quotes</h3>
                    <p className="text-xs text-[#222]/60">A Pro Plus perk — reach out to clients who marked themselves open.</p>
                  </div>
                  <span className="text-xs text-[#222]/50">{openClients.length} open</span>
                </div>
                {openClients.length === 0 ? (
                  <p className="text-sm text-[#222]/50 py-4">No open clients right now. Check back soon!</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {openClients.map(c => (
                      <div key={c.id} className="border border-black/5 rounded-xl p-4 flex items-start gap-3" data-testid={`open-client-${c.id}`}>
                        <div className="w-10 h-10 rounded-full bg-[#F2F4F2] grid place-items-center font-bold overflow-hidden">
                          {c.avatar_url ? <img src={c.avatar_url} alt="" className="w-full h-full object-cover"/> : c.name?.[0]}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm">{c.name}</p>
                          <p className="text-xs text-[#222]/60 line-clamp-2">{c.looking_for || "Looking for a pro"}</p>
                          <button onClick={() => setShowOutreach(c)} className="mt-2 text-xs text-[#245544] font-semibold flex items-center gap-1" data-testid={`outreach-${c.id}-btn`}>
                            <Send size={11}/> Send a quote
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ) : (
              <div className="mb-10">
                <ProPlusLocked
                  title="Clients looking for quotes"
                  benefit="Reach out first to neighbors who marked themselves open to quotes — Free Pros can always reply to messages and Requests."
                  Icon={Sparkles}
                  testId="locked-open-clients"
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Outreach modal */}
      {showOutreach && (
        <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4" onClick={() => setShowOutreach(null)}>
          <div className="bg-white rounded-2xl p-6 w-full max-w-md" onClick={e => e.stopPropagation()}>
            <h3 className="text-xl font-bold mb-1" style={{fontFamily:'Outfit'}}>Reach out to {showOutreach.name}</h3>
            <p className="text-sm text-[#222]/60 mb-1">Looking for: <i>{showOutreach.looking_for || "general help"}</i></p>
            <p className="text-xs text-[#222]/50 mb-3">Pro Plus members can message clients who marked themselves as open to quotes.</p>
            <textarea rows={4} value={outreachText} onChange={e => setOutreachText(e.target.value)}
              placeholder="Hi! I saw you're looking for help with…"
              className="w-full px-3 py-2 border border-black/10 rounded-xl text-sm outline-none focus:ring-2 focus:ring-[#2E6B57]"
              data-testid="outreach-textarea"/>
            <div className="flex gap-2 mt-3 justify-end">
              <button onClick={() => setShowOutreach(null)} className="px-4 py-2 text-sm" data-testid="outreach-cancel">Cancel</button>
              <button onClick={sendOutreach} disabled={!outreachText.trim()} className="btn-lime px-5 py-2 text-sm disabled:opacity-50" data-testid="outreach-send">Send quote</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
